import { isAfter, isBefore } from 'date-fns';

import { ReservationType, SectorTripNumber, UserHistoryItem } from '@user-history/entities/user-history-item';
import {
  DetailReservation, HistoryBookingDetail, ReservationAssistance, ReservationFlight, ReservationHotel, ReservationVehicle
} from '@user-history/entities/history-booking-detail';
import { getArrivalAirportCode, getDepartureDate, getFlightReservation } from '@user-history/mappers/reservation-flight.mapper';
import { CrossSellingReservationItem } from '@shared/cross-selling/entities/cross-selling';
import {
  haveCompatibleReservationsByCrossSelling, getCrossSellingUrlParams, getCrossSellingTranslates,
  getCrossSellingImage, getCrossSellingAction, isValidGeneralCrossSellingByDetail,
  isActiveProductDetailCrossSellingByConfig, getCrossSellingSource, isActiveAssistanceCrossSellingDetailByConfig
} from '@shared/cross-selling/mappers/cross-selling-general.mapper';
import {
  getAssistanceReservationSearchPayloadByCrossSelling
} from '@shared/cross-selling/mappers/cross-selling-assistance.mapper';

export function enabledForPartialCrossSelling(detail: HistoryBookingDetail, type: ReservationType): boolean {
  if (!isActiveProductDetailCrossSellingByConfig(type)) return false;
  else {
    switch (type) {
      case ReservationType.Flight:
        return isPartialEnabledCrossSellingByFlight(detail);
      default:
        return false;
    }
  }
}

export function getCrossSellingByProductItemByDetail(
  detail: HistoryBookingDetail, history: UserHistoryItem[], type: ReservationType
): CrossSellingReservationItem {
  if (enabledForPartialCrossSelling(detail, type)) {
    let reservation = null;
    let startDate = null;
    let endDate = null;
    let destination = null;
    switch (type) {
      case ReservationType.Flight:
        reservation = getFlightReservationByCrossSelling(detail);
        destination = getArrivalAirportCode(reservation);
        startDate = getFlightStartDate(reservation);
        endDate = getCrossSellingFlightEndDate(detail);
        if (isValidCrossSellingByFlights(startDate, endDate, destination, history)) {
          return crossSellingObjectByDetail(ReservationType.Hotel, startDate, endDate, destination, reservation);
        } else return null;
      default:
        return null;
    }
  } else return null;
}

export function getCrossSellingAssistanceByDetail(
  assistance: ReservationAssistance,
  detail: HistoryBookingDetail,
  type: ReservationType
): CrossSellingReservationItem {
  let reservation = null;
  let lastReservation = null;
  let startDate = new Date();
  switch (type) {
    case ReservationType.Flight:
      lastReservation = getFlightReservationByCrossSelling(detail, true);
      reservation = getFlightReservationByCrossSelling(detail);
      startDate = getDepartureDate(reservation);
      break;
    case ReservationType.Bus:
      reservation = getFlightReservation(detail);
      startDate = getDepartureDate(reservation);
      break;
    case ReservationType.Hotel:
      reservation = detail?.Reservations?.[detail.Reservations.length - 1] as ReservationHotel || {} as ReservationHotel;
      startDate = new Date(reservation?.Checkin);
      break;
    case ReservationType.Auto:
      reservation = detail?.Reservations?.[detail.Reservations.length - 1] as ReservationVehicle || {} as ReservationVehicle;
      startDate = new Date(reservation?.PickupDateTime);
      break;
    case ReservationType.AirPackage:
      reservation = (detail?.Reservations as (ReservationHotel | ReservationFlight)[])?.find(item =>
        item.ReservationType === ReservationType.Flight);
      startDate = getDepartureDate(reservation);
      break;
  }

  if (
    reservation && isActiveAssistanceCrossSellingDetailByConfig(type) &&
    isValidCrossSellingByAssistance(detail, startDate, assistance)
  ) {
    return crossSellingObjectByDetail(ReservationType.Assistance, null, null, null, reservation, lastReservation);
  } else return null;
}

function isValidCrossSellingByAssistance(
  detail: HistoryBookingDetail, startDate: Date, assistance: ReservationAssistance
): boolean {
  const today = new Date();
  const isValidGeneral = isValidGeneralCrossSellingByDetail(detail);
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  return !assistance?.Id && isValidGeneral && isAfter(startDate, today);
}

function isValidCrossSellingByFlights(
  startDate: Date, endDate: Date, destination: string, history: UserHistoryItem[]
): boolean {
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return isBefore(startDate, endDate) &&
    !haveCompatibleReservationsByCrossSelling(startDate, endDate, destination, history, ReservationType.Hotel);
}

function isPartialEnabledCrossSellingByFlight(detail: HistoryBookingDetail): boolean {
  const isRoundTrip = isRoundTripFlightByCrossSelling(detail);
  const isValidGeneral = isValidGeneralCrossSellingByDetail(detail);
  return (!!isRoundTrip && isValidGeneral);
}

function getFlightReservationByCrossSelling(detail: HistoryBookingDetail, last: boolean = false): ReservationFlight {
  const reservations = getFlightCollectionByCrossSelling(detail);
  if (reservations?.length === 0) return {} as ReservationFlight;
  else if (isBySectorInCrossSelling(detail)) {
    return last ? reservations[reservations.length - 1] as ReservationFlight : reservations[0] as ReservationFlight;
  } else return reservations[0] as ReservationFlight;
}

function crossSellingObjectByDetail(
  type: ReservationType, 
  startDate: Date, 
  endDate: Date, 
  destination: string, 
  reservation: DetailReservation,
  lastReservation?: DetailReservation,
): CrossSellingReservationItem {
  const passengers = reservation?.Passengers || [];
  return {
    ReservationType: reservation?.ReservationType,
    TypeCrossSellingItem: type,
    UrlParams: getCrossSellingUrlParams(type, startDate, endDate, destination, passengers),
    Translations: getCrossSellingTranslates(type),
    Image: getCrossSellingImage(type),
    Action: getCrossSellingAction(type),
    Source: getCrossSellingSource(type),
    AssistanceInfo: getAssistanceReservationSearchPayloadByCrossSelling(type, reservation, lastReservation),
    Currency: reservation?.Price?.CurrencyCode,
  };
}

function isRoundTripFlightByCrossSelling(detail: HistoryBookingDetail): boolean {
  const reservations = getFlightCollectionByCrossSelling(detail);
  if (reservations?.length === 0) return false;
  else if (reservations?.length === 1) {
    const reservation = getFlightReservationByCrossSelling(detail);
    return reservation.Sectors.length > 1;
  } else return isBySectorInCrossSelling(detail);
}

function getFlightStartDate(reservation: ReservationFlight): Date {
  const sectors = reservation?.Sectors ? reservation.Sectors : [];
  const departure = sectors.find(sector => sector.SectorNumber === SectorTripNumber.DEPARTURE);
  return departure ? new Date(departure.Flights[0].DepartureDate) : null;
}

function getCrossSellingFlightEndDate(detail: HistoryBookingDetail): Date {
  return getFlightEndDate(getFlightReservationByCrossSelling(detail, isBySectorInCrossSelling(detail))); 
}

function getFlightEndDate(reservation: ReservationFlight): Date {
  const sectors = reservation?.Sectors ? reservation.Sectors : [];
  const type = sectors.length === 1 ? SectorTripNumber.DEPARTURE : SectorTripNumber.RETURN;
  const arrival = sectors.find(sector => sector.SectorNumber === type);
  return arrival ? new Date(arrival.Flights[0].DepartureDate) : null;
}

function isBySectorInCrossSelling(detail: HistoryBookingDetail): boolean {
  const reservations = getFlightCollectionByCrossSelling(detail);
  return reservations?.length >= 2 && reservations.every(res => res.Sectors.length === 1);
}

function getFlightCollectionByCrossSelling(detail: HistoryBookingDetail): ReservationFlight[] {
  return detail?.Reservations ?
    (detail.Reservations as ReservationFlight[])
      .filter(res => res.ReservationType === ReservationType.Flight) : [];
}

export const exportedForTesting = {
  getCrossSellingFlightEndDate,
  crossSellingObjectByDetail
}
